import { AUTH } from '../constant';
import { getRefreshToken, storeTokens } from './authUtils';

/**
 * Exchange the stored refresh token for a fresh access token via
 * POST /v2/auth/refresh-token. Single-flight: concurrent callers (many thunks
 * firing at once, or the proactive scheduler) share one in-flight request so we
 * never stampede the endpoint or rotate the refresh token more than once.
 *
 * Returns the new access token on success, or null on any failure (missing
 * refresh token, network error, 401 invalid/expired refresh token). Callers
 * treat null as "session is over".
 */
let inFlight: Promise<string | null> | null = null;

export const refreshAccessToken = (): Promise<string | null> => {
  if (inFlight) {
    return inFlight;
  }

  inFlight = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return null;
    }
    try {
      const response = await fetch(AUTH.REFRESH_TOKEN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      if (data?.accessToken) {
        storeTokens(data.accessToken, data.refreshToken);
        return data.accessToken as string;
      }
      return null;
    } catch {
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};
